/*
 * LoggerService
 * ----------------
 * Minimal, well-documented logger service intended as a single place to
 * manage logging behavior across the application. It's purposely simple to
 * read and extend. Don't change any code elsewhere yet — this service is
 * a drop-in abstraction you can start using when you're ready.
 *
 * Features:
 * - Log levels (DEBUG, INFO, WARN, ERROR)
 * - Structured log entry shape (message, meta, level, timestamp)
 * - Pluggable sinks: by default logs to console, but you can add remote
 *   sinks (HTTP, analytics SDKs) by registering a sink function.
 * - Simple `configure()` and `addSink()` API for central configuration
 * - Safe serialization and optional redaction hook for sensitive fields
 *
 * Why this pattern?
 * - Centralizes console usage so you don't sprinkle `console.*` across files.
 * - Allows toggling behavior per environment (e.g. disable debug logs in prod)
 * - Makes it easy to add remote forwarding later (CloudWatch, Splunk,
 *   Datadog, or your own HTTP endpoint) without touching business code.
 */

import { Injectable } from '@angular/core';

export type LogMeta = Record<string, unknown> | undefined;

export enum LogLevel {
  DEBUG = 10,
  INFO = 20,
  WARN = 30,
  ERROR = 40,
}

export interface LogEntry {
  level: LogLevel;
  levelName: string;
  message: string;
  meta?: LogMeta;
  timestamp: string; // ISO string
  rawArgs?: unknown[]; // Original arguments for console output
}

/**
 * A sink receives a LogEntry and may forward it to any destination.
 * Keep sinks cheap and non-blocking; they should not throw.
 */
export type LogSink = (entry: LogEntry) => void;

export interface Logger {
  log(...args: unknown[]): void; // Direct console.log replacement
  debug(...args: unknown[]): void; // Direct console.debug replacement
  info(...args: unknown[]): void; // Direct console.info replacement
  warn(...args: unknown[]): void; // Direct console.warn replacement
  error(...args: unknown[]): void; // Direct console.error replacement

  // Runtime configuration helpers
  setLevel(level: LogLevel): void;
  addSink(sink: LogSink): void;
  removeSink(sink: LogSink): void;
  clearSinks(): void;
}

@Injectable({
  providedIn: 'root',
})
export class LoggerService implements Logger {
  private sinks: Set<LogSink> = new Set();
  private level: LogLevel = LogLevel.INFO;

  // Optional sanitizer to remove/redact sensitive values from meta before sending
  // Default: identity (no redaction). Provide a function that returns sanitized meta.
  public sanitizeMeta: (meta: LogMeta) => LogMeta = (m) => m;

  constructor() {
    // By default register a console sink that maps LogEntry to console methods.
    this.addSink(this.consoleSink.bind(this));
  }

  // -------------------- Public API --------------------
  /**
   * Direct replacement for console.log() - accepts any number of arguments
   * and logs them at INFO level. This makes migration from console.log
   * to logger.log() a simple find-and-replace operation.
   */
  log(...args: unknown[]): void {
    const message = this.formatArgs(args);
    this.logInternal(LogLevel.INFO, message, undefined, args);
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  addSink(sink: LogSink): void {
    this.sinks.add(sink);
  }

  removeSink(sink: LogSink): void {
    this.sinks.delete(sink);
  }

  clearSinks(): void {
    this.sinks.clear();
  }

  debug(...args: unknown[]): void {
    const message = this.formatArgs(args);
    this.logInternal(LogLevel.DEBUG, message, undefined, args);
  }

  info(...args: unknown[]): void {
    const message = this.formatArgs(args);
    this.logInternal(LogLevel.INFO, message, undefined, args);
  }

  warn(...args: unknown[]): void {
    const message = this.formatArgs(args);
    this.logInternal(LogLevel.WARN, message, undefined, args);
  }

  error(...args: unknown[]): void {
    const message = this.formatArgs(args);
    this.logInternal(LogLevel.ERROR, message, undefined, args);
  }

  // -------------------- Internal --------------------
  /**
   * Formats arguments into a readable string message, just like console.log does
   */
  private formatArgs(args: unknown[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return `${arg.message}\n${arg.stack || ''}`;
        if (arg === null) return 'null';
        if (arg === undefined) return 'undefined';
        try {
          return JSON.stringify(arg, null, 2);
        } catch {
          return String(arg);
        }
      })
      .join(' ');
  }

  private logInternal(level: LogLevel, message: string, meta?: LogMeta, rawArgs?: unknown[]): void {
    if (level < this.level) return; // ignore below-level messages

    const entry: LogEntry = {
      level,
      levelName: LogLevel[level],
      message,
      meta: this.safeMeta(meta),
      timestamp: new Date().toISOString(),
      ...(rawArgs && { rawArgs }),
    };

    // Fire-and-forget to all sinks. Sinks should not throw (we protect them).
    for (const sink of Array.from(this.sinks)) {
      try {
        sink(entry);
      } catch (sinkErr) {
        // If a sink fails, we don't want to break the app — log to console as last resort.
        // eslint-disable-next-line no-console
        console.error('LoggerService sink error:', sinkErr);
      }
    }
  }

  private safeMeta(meta: LogMeta): LogMeta {
    try {
      return this.sanitizeMeta(meta);
    } catch {
      // If sanitizer crashes, return a minimal safe object
      return { sanitizationError: true };
    }
  }

  // Default console sink: chooses console method based on level and uses native console features.
  // Preserves interactive console features like collapsible objects, colored output, and stack traces.
  private consoleSink(entry: LogEntry): void {
    const prefix = `[${entry.timestamp}] ${entry.levelName} -`;

    try {
      // Use raw arguments if available (preserves native console features like collapsible objects)
      // Otherwise fall back to formatted message + meta
      const args = entry.rawArgs || [entry.message, ...(entry.meta ? [entry.meta] : [])];

      // Use the appropriate console method based on log level to get native browser coloring
      switch (entry.level) {
        case LogLevel.DEBUG:
          // eslint-disable-next-line no-console
          console.debug(prefix, ...args);
          break;
        case LogLevel.INFO:
          // eslint-disable-next-line no-console
          console.log(prefix, ...args);
          break;
        case LogLevel.WARN:
          // eslint-disable-next-line no-console
          console.warn(prefix, ...args);
          break;
        case LogLevel.ERROR:
          // eslint-disable-next-line no-console
          console.error(prefix, ...args);
          break;
        default:
          // eslint-disable-next-line no-console
          console.log(prefix, ...args);
      }
    } catch {
      // ignore console write failures
    }
  }
}
